import React, { Component } from 'react';

var config = process.env;

const MEDIOS = [
    [2, 'Transferencia'],
    [1, 'Efectivo'],
];

const TRANSFERENCIA = 2;

/**
 * Saldar deuda — el modal de la cuenta corriente del equipo
 * (backend/docs/spec-cuenta-empleados.md §7.3).
 *
 * Un modal CONTROLADO por React, no el de Bootstrap: no hay `data-dismiss` ni jQuery de por medio,
 * así que no depende de que el ciclo de vida del modal y el del componente coincidan. Es la misma
 * razón por la que la caja dejó de ser un modal.
 *
 * Cuatro decisiones que no son de estilo:
 *
 *  - **El importe viene precargado con el saldo**, pero se edita: lo normal es que transfieran una
 *    parte o un número redondo. De los pagos del mes medido, casi ninguno coincidía con lo que la
 *    persona debía en ese momento.
 *  - **La fecha de la transferencia sólo se pide si el medio es transferencia.** Un pago en
 *    efectivo no tiene ninguna, y es también lo que lo deja fuera del cruce con el extracto.
 *  - **El nombre de quien registra es obligatorio** y viene precargado con el responsable de la
 *    caja abierta: el login del local es compartido y la cuenta no dice quién fue.
 *  - **Antes de confirmar dice cómo queda.** Un dígito de más se ve acá y no un mes después.
 */
class EmployeeAccountModal extends Component {

    state = {
        amount: '', paymentType: TRANSFERENCIA, transferDate: '', reference: '',
        quien: '', enviando: false, error: null, duplicado: false,
    };

    componentDidMount() {
        const { account, responsable } = this.props;
        this.setState({
            // Precargado con la deuda; si está a favor o en cero, vacío (no hay nada que saldar).
            amount: account && account.balance > 0 ? String(account.balance) : '',
            // La fecha de HOY en hora local, no `toISOString()`: en UTC-3, después de las 21:00
            // el ISO ya es el día siguiente y el campo aparecería con la fecha de mañana — con
            // el local abierto de noche, eso es todas las noches.
            transferDate: this.hoyLocal(),
            quien: responsable || '',
        });
    }

    hoyLocal = () => {
        const d = new Date();
        const dosDigitos = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`;
    };

    money = (n) => `$${Number(n || 0).toLocaleString('de-DE')}`;

    // El saldo con el que queda la persona si se confirma este importe. Es el número que evita el
    // error de tipeo, así que se calcula en vivo y no al guardar.
    saldoResultante = () => {
        const importe = Number(String(this.state.amount).replace(/[^\d.]/g, '')) || 0;
        return Number(((this.props.account.balance || 0) - importe).toFixed(2));
    };

    comoQueda = () => {
        const restante = this.saldoResultante();
        if (Math.abs(restante) < 0.005) return 'Queda en cero.';
        if (restante > 0) return `Queda debiendo ${this.money(restante)}.`;
        return `Queda ${this.money(-restante)} a favor.`;
    };

    registrar = (confirmDuplicate = false) => {
        if (this.state.enviando) return;

        if (String(this.state.amount).trim() === '') {
            this.setState({ error: 'Poné el importe del pago.' });
            return;
        }
        if (this.state.quien.trim().length < 2) {
            this.setState({ error: 'Poné quién registra el pago.' });
            return;
        }
        if (Number(this.state.paymentType) === TRANSFERENCIA && !this.state.transferDate) {
            this.setState({ error: 'Poné la fecha de la transferencia: es lo que la encuentra en el extracto.' });
            return;
        }

        this.setState({ enviando: true, error: null });

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}employeeAccount/${encodeURIComponent(this.props.account.cedula)}/payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
            body: JSON.stringify({
                amount: this.state.amount,
                paymentType: Number(this.state.paymentType),
                transferDate: Number(this.state.paymentType) === TRANSFERENCIA ? this.state.transferDate : null,
                reference: this.state.reference.trim() || null,
                created_by_name: this.state.quien.trim(),
                confirmDuplicate,
            }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.error === 0) {
                    this.props.onDone();
                    return;
                }
                // 3608: ya hay un pago igual. No es un error del operador — dos transferencias
                // iguales el mismo día son posibles — así que se ofrece confirmar en vez de
                // obligarlo a cambiar algo para poder guardar.
                this.setState({
                    enviando: false,
                    duplicado: data && data.error === 3608,
                    error: (data && data.message) || 'No se pudo registrar el pago.',
                });
            })
            .catch(() => this.setState({ enviando: false, error: 'No se pudo registrar el pago.' }));
    };

    render() {
        const { account, onClose, cajaAbierta } = this.props;
        const esTransferencia = Number(this.state.paymentType) === TRANSFERENCIA;

        return (
            <div
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1050,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                }}
                onClick={onClose}
            >
                <div
                    className="card card-small"
                    style={{ maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="card-header border-bottom">
                        <span className="font-weight-bold">Saldar deuda · {account.name || account.cedula}</span>
                    </div>

                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Debe hoy</span>
                            <span className="font-weight-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {this.money(account.balance)}
                            </span>
                        </div>

                        <div className="row">
                            <div className="col-6 mb-2">
                                <label className="d-block" style={{ fontSize: 13 }}>Importe</label>
                                <input type="text" className="form-control form-control-sm" placeholder="$0"
                                    value={this.state.amount}
                                    onChange={(e) => this.setState({ amount: e.target.value.replace(/[^\d.]/g, ''), error: null, duplicado: false })} />
                            </div>
                            <div className="col-6 mb-2">
                                <label className="d-block" style={{ fontSize: 13 }}>Medio</label>
                                <select className="form-control form-control-sm" value={this.state.paymentType}
                                    onChange={(e) => this.setState({ paymentType: Number(e.target.value), error: null })}>
                                    {MEDIOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row">
                            {/* Sólo para transferencias: un pago en efectivo no tiene fecha de
                                transferencia y pedirla obligaría a inventar una. */}
                            {esTransferencia && (
                                <div className="col-6 mb-2">
                                    <label className="d-block" style={{ fontSize: 13 }}>Fecha de la transferencia</label>
                                    <input type="date" className="form-control form-control-sm"
                                        value={this.state.transferDate}
                                        onChange={(e) => this.setState({ transferDate: e.target.value, error: null, duplicado: false })} />
                                </div>
                            )}
                            <div className={esTransferencia ? 'col-6 mb-2' : 'col-12 mb-2'}>
                                <label className="d-block" style={{ fontSize: 13 }}>Quién lo registra</label>
                                <input type="text" className="form-control form-control-sm" maxLength={60} placeholder="Nombre"
                                    value={this.state.quien}
                                    onChange={(e) => this.setState({ quien: e.target.value, error: null })} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="d-block" style={{ fontSize: 13 }}>
                                Referencia del comprobante <span className="text-muted">(opcional)</span>
                            </label>
                            <input type="text" className="form-control form-control-sm" maxLength={120}
                                placeholder={esTransferencia ? 'Banco, cuenta o número' : 'Nota'}
                                value={this.state.reference}
                                onChange={(e) => this.setState({ reference: e.target.value })} />
                        </div>

                        {String(this.state.amount).trim() !== '' && (
                            <div className="border-top pt-2 mb-2" style={{ fontSize: 13.5 }}>
                                {this.comoQueda()}
                            </div>
                        )}

                        {!cajaAbierta && (
                            <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 13 }}>
                                No hay caja abierta. El pago se registra con la caja abierta: así queda
                                dentro del arqueo del día en que se carga.
                            </div>
                        )}

                        {this.state.error && (
                            <div className={`alert py-2 mb-2 ${this.state.duplicado ? 'alert-warning' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                                {this.state.error}
                            </div>
                        )}

                        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                            <button className="btn btn-sm btn-white" onClick={onClose} disabled={this.state.enviando}>
                                Cancelar
                            </button>
                            {this.state.duplicado ? (
                                <button className="btn btn-sm btn-warning" onClick={() => this.registrar(true)} disabled={this.state.enviando}>
                                    Registrarlo igual
                                </button>
                            ) : (
                                <button className="btn btn-sm btn-primary" onClick={() => this.registrar(false)}
                                    disabled={this.state.enviando || !cajaAbierta || String(this.state.amount).trim() === ''}>
                                    Registrar pago
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default EmployeeAccountModal;
